<template>
    <div>
        <div class="subs-cards">
            <div id="preloader" v-if="load" class="preloader"></div>
            <div
                class="venue-pricing"
                v-for="(plan, index) in subPlans"
                :key="index"
                :class="{
                    'registered-user':
                        currentSubscriptionPlan === plan.subscriptionName &&
                        plan.subscriptionName === 'VenueFree',
                    'artist-pro':
                        currentSubscriptionPlan === plan.subscriptionName &&
                        plan.subscriptionName === 'VenuePro',
                    'growth-plan':
                        currentSubscriptionPlan === plan.subscriptionName &&
                        plan.subscriptionName === 'VenueGrowth',
                    'premium-plan':
                        currentSubscriptionPlan === plan.subscriptionName &&
                        plan.subscriptionName === 'VenuePremium'
                }"
            >
                <div class="plan-name">
                    {{ plan.planName }}
                </div>
                <div class="plan-price">
                    <div
                        class="plan-price-name"
                        :class="cardColors[index % cardColors.length]"
                    >
                        {{ plan.planPrice }}
                    </div>
                    <div class="plan-price-info">
                        <span class="info-text">{{ plan.planAddInfo }} </span>
                        <div class="divider"></div>
                    </div>
                </div>
                <div class="feature-info">
                    <div
                        class="feature-info-head"
                        :class="cardColors[index % cardColors.length]"
                    >
                        {{ plan.planFeatures }}
                    </div>
                    <div
                        class="sub-point-name"
                        v-if="plan.listOfPlanFeatures.SectionName.length"
                    >
                        <img :src="plan.icon" alt="/" />
                        <span class="feature-points">{{
                            plan.listOfPlanFeatures.SectionName[0]
                        }}</span>
                    </div>
                    <ul
                        class="sub-list"
                        :style="{
                            padding: !!plan.listOfPlanFeatures.SectionName.length
                                ? '0 0 0 1.75rem'
                                : '0px'
                        }"
                    >
                        <li
                            v-for="(point, index) in plan.listOfPlanFeatures.subPointsList"
                            :key="index"
                            class="feature-points"
                        >
                            <div class="sub-tooltip">{{ point.tooltip }}</div>
                            <img :src="plan.icon" alt="/" />
                            <span :style="{ color: point.subStatus ? '#DEDEDE' : '#646464' }">
                                {{ point.SubPoint }}</span
                            >
                        </li>
                    </ul>
                    <div
                        class="sub-point-name"
                        v-if="plan.listOfPlanFeatures.SectionName.length >= 2"
                    >
                        <img src="/images/global/icon-yes-art.png" alt="/" />
                        <span class="feature-points">{{
                            plan.listOfPlanFeatures.SectionName[1]
                        }}</span>
                    </div>
                    <ul class="sub-list">
                        <li
                            v-for="(point, index) in plan.listOfPlanFeatures.TalentPointsList"
                            :key="index"
                            class="feature-points"
                        >
                            <div class="sub-tooltip">{{ point.tooltip }}</div>
                            <img :src="plan.icon" alt="/" />
                            <span
                                :class="{ color: point.manageStatus ? '#DEDEDE' : '#646464' }"
                            >
                                {{ point.TalentPoint }}</span
                            >
                        </li>
                    </ul>
                    <div
                        class="sub-point-name"
                        v-if="plan.listOfPlanFeatures.SectionName.length >= 3"
                    >
                        <img :src="plan.icon" alt="/" />
                        <span class="feature-points">{{
                            plan.listOfPlanFeatures.SectionName[2]
                        }}</span>
                    </div>
                    <ul class="sub-list">
                        <li
                            v-for="(point, index) in plan.listOfPlanFeatures.ManagePointList"
                            :key="index"
                            class="feature-points"
                        >
                            <div class="sub-tooltip">{{ point.tooltip }}</div>
                            <img :src="plan.icon" alt="/" />
                            <span
                                :class="{ color: point.manageStatus ? '#DEDEDE' : '#646464' }"
                            >
                                {{ point.ManagePoint }}</span
                            >
                        </li>
                    </ul>
                </div>
                <button
                    class="call-to-action"
                    :class="btnColors[index % cardColors.length]"
                    v-if="!isAuthed"
                    @click="goLogin"
                >
                    <span
                        class="call-to-action-text"
                        :class="actionText[index % cardColors.length]"
                        >{{ plan.button }}</span
                    >
                </button>
                <button
                    v-if="currentSubscriptionPlan !== plan.subscriptionName && isAuthed"
                    @click="updateSubscriptionPlan(plan.subscriptionName)"
                    class="call-to-action"
                    :class="authColors[index % cardColors.length]"
                    :disabled="plan.authButton === 'Coming Soon'"
                >
                    <span
                        v-if="currentSubscriptionPlan == getPlanName"
                        class="call-to-action-text"
                        :class="actionText[index % cardColors.length]"
                        >{{ plan.authButton }}</span
                    >
                    <span
                        class="call-to-action-text"
                        :class="actionText[index % cardColors.length]"
                        v-else
                    >
                        {{ ctaBtnText(plan) }}</span
                    >
                </button>
            </div>
        </div>
        <button class="unlock-btn" @click="showModal">
            <span class="unlock-text">Unlock Features</span>
        </button>
        <div v-if="plan">
            <SubscriptionPaymentModal
                :show.sync="showPaymentModal"
                :role="role"
                :selectedPlanName="plan.name"
                :display-feature-text="false"
                @done="finishedSubscribe"
                @close="showModal"
            />
        </div>
        <DowngradeModal
            :open="showDowngradeModal"
            @confirm="downgradeToFree"
            @close="closeDowngradeModal"
        />
    </div>
</template>

<script>
    import { api } from '@/helpers/api'
    import SubscriptionPaymentModal from '@/components/subscriptions/PaymentModal'
    import DowngradeModal from '@/components/subscriptions/DowngradeModal'
    import { SubMixin } from '../../../mixins/subscription/subscriptions'
    import { usePaymentMethods } from '@/composables/features/payments-methods/usePayments'
    import { useAuth } from '@/composables/auth/useAuth'

    export default {
        components: {
            SubscriptionPaymentModal,
            DowngradeModal
        },
        mixins: [SubMixin],
        props: {
            isAuthed: {
                type: Boolean,
                required: false
            },
            plans: {
                type: Object,
                required: false
            },
            currentSubscriptionPlan: {
                type: String,
                required: true
            },
            userHadTrial: {
                type: Boolean,
                required: true
            }
        },
        setup() {
            const { handleShowRegisterModal } = useAuth()
            const { favPayment } = usePaymentMethods()

            return {
                favPayment,
                handleShowRegisterModal
            }
        },
        data() {
            return {
                cardColors: ['card-color1', 'card-color2', 'card-color3', 'card-color4'],
                btnColors: ['free-btn', 'pro-btn', 'growth-btn', 'premium-btn'],
                authColors: ['check-btn', 'pro-btn', 'growth-btn', 'premium-btn'],
                actionText: ['free-text', 'pro-text', 'growth-text', 'premium-text'],
                borderColors: ['border1', 'border2', 'border3', 'border4'],
                mode: 'subscriptions',
                showPaymentModal: false,
                showDowngradeModal: false,
                role: 'venue',
                load: false,
                plan: null
            }
        },
        methods: {
            goLogin() {
                this.handleShowRegisterModal()
            },
            updateSubscriptionPlan(name) {
                this.plan = this.plans.find((plan) => plan.name === name)

                if (name === 'VenueFree' || name === 'ArtistFree') {
                    return (this.showDowngradeModal = true)
                }

                const location = window.location.href
                if (location.includes('for-bookers'))
                    window.location.href = '/profile/stripe/plans/venue'
                else this.showModal()
            },
            closeDowngradeModal() {
                this.showDowngradeModal = false
            },
            finishedSubscribe() {
                this.showModal()
                window.location.reload()
            },
            async downgradeToFree() {
                await this.createSubscription()
            },
            async createSubscription() {
                try {
                    this.load = true

                    await api({
                        method: 'post',
                        url: '/profile/subscriptions/subscribe',
                        data: {
                            plan: { ...this.plan, trial_days: 1 },
                            paymentMethod: this.favPayment.card_stripe_id,
                            name: this.role
                        }
                    })
                    window.location.reload()
                } finally {
                    this.load = false
                }
            }
        },
        computed: {
            getPlanName() {
                if (this.currentSubscriptionPlan) {
                    return this.subPlans.filter(
                        (plan) => plan.subscriptionName === this.currentSubscriptionPlan
                    )[0].planName
                } else {
                    return ''
                }
            },
            ctaBtnText(self) {
                return (plan) => {
                    const currentPlan = this.subPlans.find(
                        (p) => p.planName === this.getPlanName
                    )
                    const currentPlanIndx = this.subPlans.indexOf(currentPlan)
                    const planIndx = this.subPlans.indexOf(plan)

                    if (planIndx > currentPlanIndx) {
                        return self.userHadTrial ? 'Upgrade' : 'Start for Free'
                    } else {
                        return 'Downgrade'
                    }
                }
            }
        }
    }
</script>

<style lang="scss" scoped>
    @import '../../../../sass/_variables.scss';
    .modal-subscriptions {
        align-items: center;
        background-color: #1b0d29;
        border: 1px solid #1b0d29;
        border-radius: 15px;
        display: flex;
        min-height: 638px;
        justify-content: center;
        position: fixed;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        z-index: 9999;
    }
    .sub-point-name {
        display: flex;
        flex-wrap: nowrap;
        align-items: flex-start;
        gap: 10px;
    }
    .call-to-action-text {
        color: #000;
        font-family: 'Instrument Sans';
        font-size: 18px;
        font-style: normal;
        font-weight: 650;
        line-height: 120%;
    }
    .divider {
        height: 1px;
        justify-content: center;
        align-items: center;
        align-self: stretch;
        background-color: #646464;
    }
    .sub-list {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        gap: 10px;
        margin: 0;
        width: 100%;
    }
    .registered-user {
        border: 3px solid #cdeb09;
    }
    .artist-pro {
        border: 3px solid #d8b500;
    }
    .growth-plan {
        border: 3px solid #e0939c;
    }
    .premium-plan {
        border: 3px solid #ba87ff;
    }
    li {
        list-style: none;
    }
    .subs-cards {
        display: flex;
        gap: 20px;
        align-items: stretch;
        margin-top: 96px;
    }
    .venue-pricing {
        display: flex;
        padding: 20px 25px 30px 25px;
        flex-direction: column;
        align-items: flex-start;
        align-self: stretch;
        gap: 25px;
        border-radius: 15px;
        background: #262626;
    }
    .plan-name {
        align-self: stretch;
        color: #acacac;
        font-feature-settings: 'dlig' on, 'ss10' on, 'ss05' on, 'ss07' on, 'ss08' on, 'ss03' on;
        font-family: 'Instrument Sans';
        font-size: 22px;
        font-style: normal;
        font-weight: 500;
        line-height: 120%;
        letter-spacing: -0.22px;
    }
    .plan-price {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        gap: 10px;
        align-self: stretch;
    }
    .plan-price-name {
        font-feature-settings: 'dlig' on, 'ss05' on, 'ss10' on, 'ss07' on, 'ss08' on, 'ss03' on;
        font-family: 'Instrument Sans';
        font-size: 32px;
        font-style: normal;
        font-weight: 500;
        line-height: 120%;
        letter-spacing: -0.64px;
    }
    .divider {
        width: 100%;
        height: 1px;
        background: #8b8b8b;
    }
    .plan-price-info {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        justify-content: flex-end;
        height: 80px;
        gap: 15px;
        align-self: stretch;
    }
    .info-text {
        color: #8b8b8b;
        font-feature-settings: 'dlig' on, 'ss10' on, 'ss05' on, 'ss07' on, 'ss08' on, 'ss03' on;
        font-family: 'Instrument Sans';
        font-size: 16px;
        font-style: normal;
        font-weight: 550;
        line-height: 125%;
    }
    .feature-info {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        gap: 10px;
        align-self: stretch;
        flex: 1;
        padding-bottom: 24px;
    }
    .feature-info-head {
        align-self: stretch;
        font-feature-settings: 'dlig' on, 'ss10' on, 'ss05' on, 'ss07' on, 'ss08' on, 'ss03' on;
        font-family: 'Instrument Sans';
        font-size: 18px;
        font-style: normal;
        font-weight: 550;
        line-height: 120%;
    }
    .feature-points {
        display: flex;
        align-items: center;
        flex: 1;
        gap: 10px;
        color: #dedede;
        font-feature-settings: 'dlig' on, 'ss10' on, 'ss05' on, 'ss07' on, 'ss08' on, 'ss03' on;
        font-family: 'Instrument Sans';
        font-size: 18px;
        font-style: normal;
        font-weight: 550;
        line-height: 120%;
        position: relative;
        width: 100%;
        cursor: pointer;
    }
    .sub-tooltip {
        display: none;
        position: absolute;
        bottom: 25px;
        background-color: #000;
        color: #dedede;
        padding: 10px 15px;
        border-radius: 10px;
        z-index: 1;
        font-size: 16px;
        transform: translateY(-10px);
        transition: opacity 0.3s ease, transform 0.3s ease;
        max-width: 250px;
    }
    .sub-tooltip::after {
        display: inline-block;
        content: '';
        position: absolute;
        background-color: #000;
        height: 15px;
        width: 15px;
        transform: translate(-50%, 100%) rotate(45deg); /* Добавлено translate(-50%, 100%) */
        -ms-transform: translate(-50%, 100%) rotate(45deg);
        -webkit-transform: translate(-50%, 100%) rotate(45deg);
        -o-transform: translate(-50%, 100%) rotate(45deg);
        -moz-transform: translate(-50%, 100%) rotate(45deg);
        left: 50%; /* Положение по горизонтали в 50% */
        bottom: 7px;
    }

    .feature-points:hover .sub-tooltip {
        display: block;
    }

    .card-color1 {
        color: #cdeb09;
    }
    .border1 {
        border: 3px solid #cdeb09;
    }
    .card-color2 {
        color: #d8b500;
    }
    .border2 {
        border: 3px solid #d8b500;
    }
    .card-color3 {
        color: #e0939c;
    }
    .border3 {
        border: 3px solid #e0939c;
    }
    .card-color4 {
        color: #ba87ff;
    }
    .border4 {
        border: 3px solid #ba87ff;
    }
    .img {
        margin-right: 10px;
    }
    .call-to-action {
        display: flex;
        justify-content: center;
        align-items: center;
        align-self: stretch;
        padding: 11px 30px 12px 30px;
        border-radius: 100px;
        border: none;
    }
    .check-btn {
        display: block;
        background: #cdeb09;
    }
    .free-btn {
        background: #cdeb09;
    }
    .pro-btn {
        background: #d8b500;
    }
    .growth-btn {
        background: #e0939c;
    }
    .premium-btn {
        background: #ba87ff;
    }
    .call-to-action-text {
        font-family: 'Instrument Sans';
        font-size: 18px;
        font-style: normal;
        font-weight: 550;
        line-height: 120%;
    }
    .free-text {
        color: #000;
    }
    .pro-text {
        color: #000;
    }
    .growth-text {
        color: #000;
    }
    .premium-text {
        color: #000;
    }
    .disaeble-point {
        display: flex;
        align-items: flex-start;
        flex: 1;
        color: #646464;
        font-family: 'Instrument Sans';
        font-size: 18px;
        font-style: normal;
        font-weight: 550;
        line-height: 120%;
    }
    .unlock-btn {
        display: none;
        padding: 11px 30px 12px 30px;
        justify-content: center;
        align-items: center;
        border-radius: 100px;
        border: 1px solid #cdeb09;
        background: #121212;
    }
    .unlock-text {
        color: #cdeb09;
        font-family: 'Instrument Sans';
        font-size: 18px;
        font-style: normal;
        font-weight: 650;
        line-height: 120%;
    }
    @media screen and (max-width: 980px) {
        .subs-cards {
            flex-wrap: wrap;
            justify-content: center;
            margin-top: 40px !important;
        }
        .venue-pricing {
            max-width: 400px;
        }
    }
</style>
